import * as React from 'react';
import { buildAdapter } from './grouped-table/helper';
import { Configure, Index, InstantSearch, ClearRefinements } from 'react-instantsearch';
import NvChartHitsWidget from './nv-chart/partials/NvChartHitsWidget';
import Filter from './nv-chart/partials/Filter';
import { chartStyles, Config, filtersConfig, futureFlags, typesenseClientConfig, UIState } from './nv-chart/helpers';
import Preloader from './nv-chart/partials/Preloader';
import { FilterButton } from './faceted-search/react/helper';
import Offcanvas from './faceted-search/react/Offcanvas';
import OffcanvasContent from './nv-chart/partials/OffcanvasContent';
import GroupDropdown from './nv-chart/partials/GroupDropdown';
import GroupLabel from './nv-chart/partials/GroupLabel';

export default function NvChart(props) {
  const {settings} = props;

  const {
    title,
    defaultIndex,
    style,
    lineStyle,
    groups = []
  } = settings;

  if (groups.length === 0) {
    return (
      <Preloader title='Chart'/>
    );
  }

  const [groupLabel, setGroupLabel] = React.useState(groups[0].label);
  const [isReady, setIsReady] = React.useState(false);
  const css = chartStyles(style);

  return (
    <div className='nv-chart-groups'>
      {groups.map(group => {
        if (group.label !== groupLabel) {
          return null;
        }

        const adapter = React.useMemo(() => buildAdapter(defaultIndex, typesenseClientConfig), [defaultIndex]);

        if (!adapter) {
          return (
            <Preloader key={group.label} title='Chart'/>
          );
        }

        const ref = React.useRef(null);

        let delay = 0;
        let delayDelta = 400;

        return (
          <div className='nv-chart-group' key={group.label} ref={ref}>
            <InstantSearch
              searchClient={adapter.searchClient}
              indexName={defaultIndex}
              {...(UIState(group, defaultIndex)) ? {initialUiState: UIState(group, defaultIndex)} : {}}
              future={futureFlags()}>
              <Index indexName={defaultIndex}>
                <Configure hitsPerPage={250} {...(filtersConfig(group) ? {filters: filtersConfig(group)} : {})}/>
                <div className={css.root}>
                  <Offcanvas title={'Filters'} callback={() => {
                    ref.current.querySelector('.clear-group-filters button')?.click();
                    const filterContainers = ref.current.querySelectorAll('.desktop-filters [data-attribute]');
                    delay = 0;
                    setIsReady(false);
                    for (const container of filterContainers) {
                      setTimeout((d) => {
                        if (d >= (group.filters.length - 1) * delayDelta) {
                          setIsReady(true);
                        }
                        const kind = container.getAttribute('data-kind');
                        switch (kind) {
                          case 'radio-buttons':
                            container.querySelector('.with-value input').click();
                            break;
                          case 'checkboxes':
                            container.querySelector('.with-value input').click();
                            break;
                        }

                      }, delay, delay);
                      delay += delayDelta;
                    }
                  }}>
                    <OffcanvasContent>
                      <div className='nv-chart__filters'>
                        <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={setGroupLabel} setIsReady={setIsReady}/>
                        {group.filters.length > 0 && group.filters.map((filter) => {
                          return <Filter
                            group={group}
                            key={filter.attribute}
                            config={filter}/>
                        })}
                      </div>
                    </OffcanvasContent>
                  </Offcanvas>
                  <div className='container'>
                    {!isReady && <div className='floating-preloader'><Preloader/></div>}
                    <div className='row'>
                      <div className='col-lg-3 d-none d-lg-block d-xl-block d-xxl-block'>
                        <div className='nv-chart__filters desktop-filters'>
                          <div className='d-none clear-group-filters'>
                            <ClearRefinements/>
                          </div>
                          <GroupDropdown groupLabel={groupLabel} groups={groups} valueSetter={setGroupLabel} setIsReady={setIsReady}/>
                          <h3 className='h--smaller my-0 nv-chart__filters-heading'>{Config.I18n.Filters.Title}</h3>
                          {group.filters.length > 0 && group.filters.map((filter) => {
                            return (
                              <Filter
                                key={filter.attribute}
                                group={group}
                                config={filter}
                                onLoad={(attribute) => {
                                  delay += delayDelta;
                                  setTimeout((d) => {
                                    if(d > (group.filters.length - 1) * delayDelta){
                                      setIsReady(true);
                                    }
                                    if (!ref.current) {
                                      return;
                                    }
                                    const filterContainer = ref.current.querySelector('.desktop-filters [data-attribute="' + attribute + '"]');
                                    if (!filterContainer) {
                                      return;
                                    }
                                    const kind = filterContainer.getAttribute('data-kind');
                                    switch (kind){
                                      case 'radio-buttons':
                                        filterContainer.querySelector('.with-value input').click();
                                        break;
                                      case 'checkboxes':
                                        filterContainer.querySelector('.with-value input').click();
                                        break;
                                    }
                                  }, delay, delay);
                                }}
                                />
                            );
                          })}
                        </div>
                      </div>
                      <div className='col-lg-9'>
                        <div className='nv-chart__header'>
                          <h3 className='h--small my-0'>{title}</h3>
                          <FilterButton/>
                        </div>
                        <div className='nv-chart__view'>
                          <div className='text-center mb-3'>
                            <div className='nv-chart__group-description'>
                              <h3 className='h--smallest'>{group.description}</h3>
                            </div>
                            <div className='nv-chart__group-label'>
                              <GroupLabel currentGroup={group}/>
                            </div>
                          </div>
                          <NvChartHitsWidget chartConfig={group} lineStyle={lineStyle}/>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Index>
            </InstantSearch>
          </div>
        );
      })}
    </div>
  );
}
